import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Form, Button, Alert, Card, Spinner, ProgressBar, Badge, OverlayTrigger, Tooltip as BSTooltip, Modal } from 'react-bootstrap';
import confetti from 'canvas-confetti';
import { colors } from '../brand-colors';
import SpeechInput from '../components/SpeechInput';
import DecisionGate from '../components/mind-to-method/DecisionGate';
import TransitionScreen from '../components/mind-to-method/TransitionScreen';
import analyticsService from '../services/analyticsService';

// Brand-styled button styles
const brandButtonStyle = {
  background: `linear-gradient(180deg, ${colors.redDark} 0%, ${colors.greyDarkest} 100%)`,
  border: 'none',
  color: colors.greyLightest,
  fontWeight: '600',
  padding: '0.75rem 2rem',
  fontSize: '1.1rem',
  transition: 'all 0.2s ease',
};

const brandButtonHoverStyle = {
  background: `linear-gradient(180deg, ${colors.red} 0%, ${colors.greyDarker} 100%)`,
};

const successButtonStyle = {
  background: `linear-gradient(180deg, ${colors.goldMid} 0%, ${colors.goldDark} 100%)`,
  border: 'none',
  color: colors.greyDarkest,
  fontWeight: '600',
  padding: '0.75rem 2rem',
  fontSize: '1.1rem',
  transition: 'all 0.2s ease',
};

const successButtonHoverStyle = {
  background: `linear-gradient(180deg, ${colors.goldLight} 0%, ${colors.gold} 100%)`,
};

// =====================================
// V2 Discovery Data (IP Categories)
// =====================================
const IP_CATEGORIES = [
  { id: 'processes', name: 'Internal Processes or Workflows', shortName: 'Processes & Workflows', icon: '⚙️', description: 'Custom systems for operations.' },
  { id: 'cultural', name: 'Cultural or Experiential IP', shortName: 'Cultural & Experiential', icon: '🌍', description: 'Rare skills from your background.' },
  { id: 'training', name: 'Training or Educational Content', shortName: 'Training & Education', icon: '📚', description: 'Guides and methods you\'ve created.' },
  { id: 'mental_models', name: 'Unique Mental Models or Heuristics', shortName: 'Mental Models', icon: '🧠', description: 'Your decision-making shortcuts.' },
  { id: 'customer_insights', name: 'Customer Insights or Data Patterns', shortName: 'Customer Insights', icon: '📊', description: 'Patterns in customer behavior.' },
  { id: 'branding', name: 'Branding or Marketing Nuances', shortName: 'Branding & Marketing', icon: '🎨', description: 'Signature storytelling approaches.' },
  { id: 'inventions', name: 'Unpatented Inventions or Ideas', shortName: 'Inventions & Ideas', icon: '💡', description: 'Novel concepts you\'ve prototyped.' },
  { id: 'supplier', name: 'Supplier or Network Know-How', shortName: 'Network Know-How', icon: '🤝', description: 'Insider tips on partnerships.' },
  { id: 'software', name: 'Software Tweaks or Digital Tools', shortName: 'Software & Tools', icon: '🔧', description: 'Custom scripts and automations.' },
  { id: 'design', name: 'Design or Aesthetic Innovations', shortName: 'Design Innovations', icon: '✨', description: 'Unique layouts or UI tweaks.' }
];

const WARMUP_QUESTIONS = [
  "What's the one thing your clients or customers always compliment you on?",
  "If you had to teach a new employee 'the secret sauce' of how things really work here, what would you focus on first?",
];

const CATEGORY_QUESTIONS = {
  processes: [
    "Walk me through your most important custom workflow - the one that really sets you apart.",
    "How did you develop this process?",
    "How much time or money does this save compared to the standard way?"
  ],
  cultural: [
    "What unique background or traditions influence how you approach your work?",
    "How does this give you an edge others don't have?"
  ],
  training: [
    "What training materials or methods have you created that consistently get results?",
    "Could this training content be packaged and sold?"
  ],
  mental_models: [
    "Describe your 'gut feel' rule for making key decisions.",
    "When facing a common challenge, how does your approach differ?"
  ],
  customer_insights: [
    "What patterns have you noticed in customer behavior that guide your strategies?",
    "Could these insights be turned into a predictive model?"
  ],
  branding: [
    "What unique branding elements do you have?",
    "How did you develop your positioning?"
  ],
  inventions: [
    "Describe the novel idea you've developed but never documented.",
    "What specific problem does this solve?"
  ],
  supplier: [
    "What insider knowledge do you have about working with vendors?",
    "What advantage does this give you?"
  ],
  software: [
    "Describe the custom script or automation you've created.",
    "How much time does this save?"
  ],
  design: [
    "What unique design elements have you created?",
    "How does this improve the experience?"
  ]
};

const DISCOVERY_ORDER = ['processes', 'cultural', 'training', 'mental_models', 'customer_insights'];

// Glossary for V1 flow
const GLOSSARY = {
  'intellectual property': 'Your unique expertise, frameworks, and methods that set you apart from competitors.',
  'IP': 'Short for Intellectual Property - your unique expertise and methods.',
  'codified': 'Written down and organized in a clear, teachable format.',
  'methodology': 'Your systematic approach or process for solving a specific problem.',
};

const Term = ({ term, children }) => {
  const definition = GLOSSARY[term.toLowerCase()] || GLOSSARY[term];
  if (!definition) return children || term;
  return (
    <OverlayTrigger placement="top" overlay={<BSTooltip style={{ maxWidth: '300px' }}>{definition}</BSTooltip>}>
      <span style={{ borderBottom: '1px dotted #666', cursor: 'help' }}>{children || term}</span>
    </OverlayTrigger>
  );
};

/**
 * MindToMethodUnified - Unified IP Extraction Flow
 * Combines V1 (deep extraction) and V2 (discovery) into a single user journey
 *
 * Flow:
 * 1. CAPTURE - name, email, company, industry
 * 2. DECISION_GATE - "Have IP" or "Explore"
 * 3A. HAVE_IP_FLOW - V1 extraction (wakeup, overview, extraction, naming, output)
 * 3B. EXPLORE_FLOW - V2 discovery (category selection, interview, output with IP assets)
 *     -> TRANSITION - Select IP to codify
 *     -> CODIFY_FLOW - V1 extraction with selected IP as context
 * 4. OUTPUT - Final polished framework
 */
function MindToMethodUnified() {
  // ==========================================
  // CORE STATE
  // ==========================================
  const [phase, setPhase] = useState('capture');
  // capture -> kg_check_offer -> (kg_check_results ->) decision_gate -> have_ip_flow / explore_flow -> (transition ->) codify_flow -> output
  const [wantsKgCheck, setWantsKgCheck] = useState(null); // null = not decided, true = yes, false = no

  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [existingSession, setExistingSession] = useState(null);
  const [showVideoModal, setShowVideoModal] = useState(false);

  // User data
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    industry: ''
  });

  // Flow path: 'have_ip' or 'explore'
  const [flowPath, setFlowPath] = useState(null);
  const [transitionedFromV2, setTransitionedFromV2] = useState(false);
  const [selectedIpAsset, setSelectedIpAsset] = useState(null);

  // ==========================================
  // V1 (HAVE_IP) FLOW STATE
  // ==========================================
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [currentSection, setCurrentSection] = useState(1);
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [questionKey, setQuestionKey] = useState('');
  const [sectionTitle, setSectionTitle] = useState('');
  const [progress, setProgress] = useState(0);
  const [samples, setSamples] = useState([]);
  const [userAnswer, setUserAnswer] = useState('');
  const answerBeforeSpeechRef = useRef('');
  const [coachingResponse, setCoachingResponse] = useState('');
  const [attemptsRemaining, setAttemptsRemaining] = useState(4);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [namingOptions, setNamingOptions] = useState(null);
  const [synopsis, setSynopsis] = useState(null);
  const [selectedName, setSelectedName] = useState('');
  const [customName, setCustomName] = useState('');
  const [namingChoice, setNamingChoice] = useState('');
  const [proposedName, setProposedName] = useState('');

  // ==========================================
  // V2 (EXPLORE) FLOW STATE
  // ==========================================
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [discoveryMode, setDiscoveryMode] = useState(false);
  const [currentDiscoveryIndex, setCurrentDiscoveryIndex] = useState(0);
  const [v2Questions, setV2Questions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [v2Answers, setV2Answers] = useState({});
  const [currentV2Answer, setCurrentV2Answer] = useState('');
  const [v2Output, setV2Output] = useState(null);

  // ==========================================
  // OUTPUT STATE
  // ==========================================
  const [output, setOutput] = useState(null);
  const [pdfSending, setPdfSending] = useState(false);
  const [pdfSent, setPdfSent] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [updatedEmail, setUpdatedEmail] = useState('');

  const answerInputRef = useRef(null);
  const conversationEndRef = useRef(null);

  // Auto-scroll conversation
  const scrollToBottom = () => {
    conversationEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [conversationHistory]);

  // ==========================================
  // FORM HANDLERS
  // ==========================================
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };


  // ==========================================
  // SESSION MANAGEMENT
  // ==========================================
  const handleStart = async () => {
    if (!formData.name.trim()) {
      setError('Please enter your name');
      return;
    }
    if (!formData.email.trim() || !formData.email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/mind-to-method/start-unified', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim().toLowerCase(),
          company: formData.company.trim(),
          industry: formData.industry.trim()
        })
      });

      const data = await response.json();

      if (data.success) {
        setSessionId(data.session_id);

        // Track session start
        analyticsService.mind2method.started({
          session_id: data.session_id,
          is_existing: data.is_existing,
          has_company: !!formData.company.trim(),
          has_industry: !!formData.industry.trim()
        });

        if (data.is_existing) {
          // Restore existing session
          await restoreSession(data);
        } else {
          // New session - offer knowledge graph check first
          setPhase('kg_check_offer');
        }
      } else {
        setError(data.error || 'Failed to start session');
      }
    } catch (err) {
      setError('Connection failed. Please try again.');
    }

    setLoading(false);
  };

  const restoreSession = async (data) => {
    if (data.output_generated) {
      setPhase('output');
      await loadOutput(data.session_id);
    } else if (data.flow_path) {
      setFlowPath(data.flow_path);
      setTransitionedFromV2(data.transitioned_from_v2 || false);

      // Restore to appropriate phase based on status
      if (data.status === 'codify_transition' || data.transitioned_from_v2) {
        setPhase('codify_flow');
        await continueFromQuestion(data.session_id);
      } else if (data.flow_path === 'have_ip') {
        setPhase('have_ip_flow');
        await loadV1State(data.session_id);
      } else if (data.flow_path === 'explore') {
        setPhase('explore_flow');
        await loadV2State(data.session_id);
      }
    } else {
      setPhase('decision_gate');
    }
  };

  // ==========================================
  // KNOWLEDGE GRAPH CHECK (Optional)
  // ==========================================
  const handleKgCheckChoice = async (wantsCheck) => {
    setWantsKgCheck(wantsCheck);

    // Track KG check decision
    analyticsService.mind2method.kgCheckViewed({ opted_in: wantsCheck });

    if (wantsCheck) {
      // Run the knowledge graph check
      setPhase('kg_check_results');
      runPhase0();
    } else {
      // Skip to decision gate
      setPhase('decision_gate');
    }
  };

  const handleKgCheckContinue = () => {
    // After viewing KG results, proceed to decision gate
    setPhase('decision_gate');
  };

  // ==========================================
  // DECISION GATE
  // ==========================================
  const handleSelectPath = async (path) => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`/api/mind-to-method/set-flow-path/${sessionId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ flow_path: path })
      });

      const data = await response.json();

      if (data.success) {
        setFlowPath(path);

        // Track path selection
        analyticsService.mind2method.pathSelected(path, {
          session_id: sessionId,
          did_kg_check: wantsKgCheck === true
        });

        if (path === 'have_ip') {
          // If user already did KG check, skip wakeup and go to overview
          if (wantsKgCheck === true && searchResults) {
            setPhase('have_ip_overview');
          } else if (wantsKgCheck === false) {
            // User skipped KG check, go directly to overview
            setPhase('have_ip_overview');
          } else {
            // Fallback - run wakeup
            setPhase('have_ip_wakeup');
            runPhase0();
          }
        } else {
          setPhase('explore_category');
        }
      } else {
        setError(data.error || 'Failed to set path');
      }
    } catch (err) {
      setError('Connection failed. Please try again.');
    }

    setLoading(false);
  };

  // ==========================================
  // V1 (HAVE_IP) FLOW FUNCTIONS
  // ==========================================
  const runPhase0 = async () => {
    setSearchLoading(true);
    try {
      const response = await fetch(`/api/mind-to-method/phase0/${sessionId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (data.success) {
        setSearchResults(data.results);
      } else {
        setSearchResults({ error: true });
      }
    } catch (err) {
      setSearchResults({ error: true });
    }
    setSearchLoading(false);
  };

  const startV1Extraction = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/mind-to-method/start-extraction/${sessionId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (data.success) {
        setCurrentSection(data.section);
        setSectionTitle(data.section_title);
        setProgress(data.progress);
        setCurrentQuestion(data.question);
        setQuestionKey(data.question_key);
        setSamples(data.samples || []);
        setPhase('have_ip_extraction');
        setCoachingResponse('');
        setUserAnswer('');
        setConversationHistory([{ role: 'assistant', content: data.question }]);
      } else {
        setError(data.error || 'Failed to start extraction');
      }
    } catch (err) {
      setError('Connection failed. Please try again.');
    }
    setLoading(false);
  };

  const continueFromQuestion = async (id) => {
    try {
      const response = await fetch(`/api/mind-to-method/resume-extraction/${id}`);
      const data = await response.json();
      if (data.success) {
        setCurrentSection(data.section);
        setSectionTitle(data.section_title);
        setProgress(data.progress);
        setCurrentQuestion(data.question);
        setQuestionKey(data.question_key);
        setSamples(data.samples || []);
        setConversationHistory([{ role: 'assistant', content: data.question }]);
        setPhase(transitionedFromV2 ? 'codify_extraction' : 'have_ip_extraction');
      } else {
        startV1Extraction();
      }
    } catch (err) {
      startV1Extraction();
    }
  };

  const handleSubmitV1Answer = async (answerOverride = null) => {
    const answerToSubmit = answerOverride || userAnswer.trim();
    if (!answerToSubmit || !questionKey) {
      setError('Please provide an answer');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`/api/mind-to-method/answer/${sessionId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          answer: answerToSubmit,
          question_key: questionKey
        })
      });

      const data = await response.json();

      if (data.success) {
        const newHistory = [...conversationHistory, { role: 'user', content: answerToSubmit }];

        if (data.needs_coaching) {
          setCoachingResponse(data.coaching_response);
          setAttemptsRemaining(data.attempts_remaining);
          newHistory.push({ role: 'assistant', content: data.coaching_response });
          setConversationHistory(newHistory);
          setUserAnswer('');
          if (data.question_key) setQuestionKey(data.question_key);
        } else if (data.completed) {
          setConversationHistory(newHistory);
          if (data.has_framework_name && data.framework_name) {
            setPhase('output');
            await generateV1Output();
          } else {
            setPhase('naming');
            await loadNamingOptions();
          }
        } else {
          // Track step completion
          analyticsService.mind2method.stepCompleted(data.section, data.section_title, {
            progress: data.progress,
            flow_path: flowPath
          });

          setCurrentSection(data.section);
          setSectionTitle(data.section_title);
          setProgress(data.progress);
          setCurrentQuestion(data.question);
          setQuestionKey(data.question_key);
          setSamples(data.samples || []);
          setCoachingResponse('');
          setUserAnswer('');
          setAttemptsRemaining(4);
          setNamingChoice('');
          setProposedName('');
          newHistory.push({ role: 'assistant', content: data.question });
          setConversationHistory(newHistory);
        }
      } else {
        setError(data.error || 'Failed to submit answer');
      }
    } catch (err) {
      setError('Connection failed. Please try again.');
    }

    setLoading(false);
    setTimeout(() => answerInputRef.current?.focus(), 100);
  };

  const loadNamingOptions = async () => {
    try {
      const response = await fetch(`/api/mind-to-method/generate-names/${sessionId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (data.success) {
        setNamingOptions(data.naming_options);
        if (data.synopsis) setSynopsis(data.synopsis);
      }
    } catch (err) {
      setNamingOptions({ descriptive: ['The Framework Method'], action_oriented: ['Impact System'], metaphor_based: ['The Blueprint'] });
    }
  };

  const handleSetName = async () => {
    const name = selectedName === 'custom' ? customName.trim() : selectedName;
    if (!name) {
      setError('Please select or enter a name');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`/api/mind-to-method/set-framework-name/${sessionId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name })
      });
      const data = await response.json();
      if (data.success) {
        setPhase('output');
        await generateV1Output(sessionId);
      } else {
        setError(data.error || 'Failed to save name');
      }
    } catch (err) {
      setError('Connection failed. Please try again.');
    }
    setLoading(false);
  };

  const generateV1Output = async (id = sessionId) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/mind-to-method/generate-output/${id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (data.success) {
        setOutput(data.output);

        // Track completion
        analyticsService.mind2method.completed(data.output?.frameworkName || 'Unknown', {
          session_id: id,
          flow_path: flowPath,
          transitioned_from_v2: transitionedFromV2
        });
      } else {
        setError(data.error || 'Failed to generate output');
      }
    } catch (err) {
      setError('Connection failed. Please try again.');
    }
    setLoading(false);
  };

  const loadV1State = async (id) => {
    try {
      const response = await fetch(`/api/mind-to-method/get-session/${id}`);
      const data = await response.json();
      if (data.success) {
        const session = data.session;
        if (session.ai_search_results) {
          setSearchResults(session.ai_search_results);
        }
        if (session.current_section && session.current_question) {
          await continueFromQuestion(id);
        } else {
          setPhase('have_ip_wakeup');
        }
      }
    } catch (err) {
      setPhase('have_ip_wakeup');
    }
  };

  // ==========================================
  // V2 (EXPLORE) FLOW FUNCTIONS
  // ==========================================
  const handleCategorySelect = (categoryId) => {
    setSelectedCategory(categoryId);
    setDiscoveryMode(false);
  };

  const handleDiscoveryMode = () => {
    setSelectedCategory(null);
    setDiscoveryMode(true);
  };

  const startV2Interview = async () => {
    setLoading(true);

    let questionList = [];

    // Warmup questions
    WARMUP_QUESTIONS.forEach((q, idx) => {
      questionList.push({ type: 'warmup', question: q, key: `warmup_${idx}` });
    });

    if (discoveryMode) {
      const firstCategory = DISCOVERY_ORDER[0];
      (CATEGORY_QUESTIONS[firstCategory] || []).slice(0, 3).forEach((q, idx) => {
        questionList.push({ type: 'category', category: firstCategory, question: q, key: `${firstCategory}_${idx}` });
      });
    } else if (selectedCategory && CATEGORY_QUESTIONS[selectedCategory]) {
      CATEGORY_QUESTIONS[selectedCategory].forEach((q, idx) => {
        questionList.push({ type: 'category', category: selectedCategory, question: q, key: `${selectedCategory}_${idx}` });
      });
    }

    setV2Questions(questionList);
    setCurrentQuestionIndex(0);
    setConversationHistory([{
      role: 'assistant',
      content: `Great, ${formData.name}! Let's discover your hidden intellectual property.\n\n${questionList[0].question}`
    }]);
    setPhase('explore_interview');
    setLoading(false);
  };

  const handleSubmitV2Answer = async () => {
    if (!currentV2Answer.trim()) {
      setError('Please provide an answer');
      return;
    }

    setLoading(true);
    setError('');

    const currentQ = v2Questions[currentQuestionIndex];
    const newHistory = [...conversationHistory, { role: 'user', content: currentV2Answer }];
    const newAnswers = { ...v2Answers, [currentQ.key]: currentV2Answer };
    setV2Answers(newAnswers);

    // Move to next question or finish
    if (currentQuestionIndex + 1 < v2Questions.length) {
      const nextQ = v2Questions[currentQuestionIndex + 1];
      newHistory.push({ role: 'assistant', content: `Good insight. ${nextQ.question}` });
      setConversationHistory(newHistory);
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setCurrentV2Answer('');
    } else {
      // Generate V2 output
      newHistory.push({ role: 'assistant', content: "Excellent! Let me analyze what you've shared and extract your key IP assets..." });
      setConversationHistory(newHistory);
      await generateV2Output(newAnswers);
    }

    setLoading(false);
  };

  const generateV2Output = async (answers) => {
    setLoading(true);

    // Simulate AI analysis and generate IP assets
    const ipAssets = [];
    const category = selectedCategory || DISCOVERY_ORDER[0];
    const categoryInfo = IP_CATEGORIES.find(c => c.id === category);

    // Generate mock IP assets based on answers
    if (Object.keys(answers).length > 0) {
      ipAssets.push({
        name: `Your ${categoryInfo?.shortName || 'IP'} Framework`,
        category: category,
        description: answers[`${category}_0`] || answers['warmup_0'] || 'A unique approach based on your expertise',
        value: 'high',
        next_steps: 'Codify this into a complete framework'
      });

      if (answers['warmup_1']) {
        ipAssets.push({
          name: 'Your Secret Sauce Method',
          category: 'training',
          description: answers['warmup_1'],
          value: 'medium',
          next_steps: 'Document as training material'
        });
      }
    }

    setV2Output({
      ip_assets: ipAssets,
      summary: `We've identified ${ipAssets.length} potential IP assets from your expertise.`,
      recommendations: [
        'Choose one IP asset to codify into a complete framework',
        'The deep extraction will take about 10-15 minutes',
        'You\'ll get a PDF document of your framework'
      ]
    });

    setPhase('explore_output');
    setLoading(false);
  };

  // ==========================================
  // TRANSITION (V2 -> V1)
  // ==========================================
  const handleSelectIpToCodify = (ipAsset) => {
    setSelectedIpAsset(ipAsset);
    setPhase('transition');
  };

  const handleStartCodification = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`/api/mind-to-method/transition-to-codify/${sessionId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ selected_ip: selectedIpAsset })
      });

      const data = await response.json();

      if (data.success) {
        setTransitionedFromV2(true);
        // Start V1 extraction from specific question
        await continueFromQuestion(sessionId);
        setPhase('codify_extraction');
      } else {
        setError(data.error || 'Failed to start codification');
      }
    } catch (err) {
      setError('Connection failed. Please try again.');
    }

    setLoading(false);
  };

  const loadV2State = async (id) => {
    // For now, just go to category selection
    setPhase('explore_category');
  };

  const loadOutput = async (id) => {
    try {
      const response = await fetch(`/api/mind-to-method/generate-output/${id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (data.success) {
        setOutput(data.output);
      }
    } catch (err) {
      setError('Failed to load output');
    }
  };

  // ==========================================
  // PDF HANDLING
  // ==========================================
  const handleSendPdf = async (useUpdatedEmail = false) => {
    setPdfSending(true);
    setEmailError('');

    const emailToUse = useUpdatedEmail && updatedEmail ? updatedEmail.trim().toLowerCase() : null;

    try {
      const response = await fetch(`/api/mind-to-method/send-pdf/${sessionId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(emailToUse ? { email: emailToUse } : {})
      });

      const data = await response.json();

      if (data.success) {
        setPdfSent(true);
        if (emailToUse) setFormData(prev => ({ ...prev, email: emailToUse }));

        // Track PDF sent
        analyticsService.mind2method.pdfSent({ session_id: sessionId });
      } else {
        setEmailError(data.error || 'Failed to send PDF.');
        setUpdatedEmail(formData.email);
      }
    } catch (err) {
      setEmailError('Failed to send PDF. Please try again.');
      setUpdatedEmail(formData.email);
    }

    setPdfSending(false);
  };

  // Confetti on output
  useEffect(() => {
    if (phase === 'output' && output && !loading) {
      const end = Date.now() + 1500;
      const confettiColors = ['#a10000', '#ba1d1d', '#db3838', '#ddb75e', '#cca557', '#ba934f'];
      (function frame() {
        confetti({ particleCount: 3, angle: 60, spread: 55, origin: { x: 0 }, colors: confettiColors });
        confetti({ particleCount: 3, angle: 120, spread: 55, origin: { x: 1 }, colors: confettiColors });
        if (Date.now() < end) requestAnimationFrame(frame);
      }());
    }
  }, [phase, output, loading]);

  // Trigger Phase 0 when entering wakeup
  useEffect(() => {
    if (phase === 'have_ip_wakeup' && sessionId && !searchResults) {
      runPhase0();
    }
  }, [phase, sessionId]);

  // ==========================================
  // RENDER: CAPTURE PHASE
  // ==========================================
  if (phase === 'capture') {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={8} lg={6}>
            <Card className="shadow">
              <Card.Body className="p-5">
                <h1 className="text-center mb-2" style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1a365d' }}>
                  Mind to Method
                </h1>
                <p className="text-center text-muted mb-4">
                  Discover and <Term term="codified">codify</Term> your unique <Term term="intellectual property">intellectual property</Term>
                </p>

                {/* Video CTA Button */}
                <div className="text-center mb-4">
                  <Button
                    variant="link"
                    onClick={() => {
                      analyticsService.mind2method.videoWatched();
                      setShowVideoModal(true);
                    }}
                    style={{
                      fontSize: '1.1rem',
                      fontWeight: '600',
                      color: colors.redDark,
                      textDecoration: 'none',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <span style={{ fontSize: '1.5rem' }}>▶️</span>
                    Discover WHY This Changes Everything
                  </Button>
                </div>

                {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

                <Form.Group className="mb-3">
                  <Form.Label><strong>Your Name</strong> <span className="text-danger">*</span></Form.Label>
                  <Form.Control type="text" placeholder="e.g., Sarah Johnson" value={formData.name} onChange={(e) => handleChange('name', e.target.value)} />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label><strong>Email Address</strong> <span className="text-danger">*</span></Form.Label>
                  <Form.Control type="email" placeholder="your@email.com" value={formData.email} onChange={(e) => handleChange('email', e.target.value)} />
                  <Form.Text className="text-muted">We'll send your framework here.</Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label><strong>Company Name</strong></Form.Label>
                  <Form.Control type="text" placeholder="e.g., Acme Consulting" value={formData.company} onChange={(e) => handleChange('company', e.target.value)} />
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label><strong>Industry/Expertise Area</strong></Form.Label>
                  <Form.Control type="text" placeholder="e.g., Marketing Strategy" value={formData.industry} onChange={(e) => handleChange('industry', e.target.value)} />
                </Form.Group>

                <Button size="lg" className="w-100" onClick={handleStart} disabled={loading} style={brandButtonStyle}
                  onMouseEnter={(e) => Object.assign(e.target.style, brandButtonHoverStyle)}
                  onMouseLeave={(e) => Object.assign(e.target.style, brandButtonStyle)}>
                  {loading ? <><Spinner size="sm" className="me-2" />Starting...</> : 'Start IP Extraction'}
                </Button>

                <p className="text-center text-muted mt-4" style={{ fontSize: '0.85rem' }}>
                  Takes 10-15 minutes. Your progress is saved automatically.
                </p>

                <p className="text-center mt-3" style={{ fontSize: '0.85rem', color: '#666' }}>
                  <strong>Returning?</strong> Just enter the same name and email to pick up where you left off.
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Video Modal */}
        <Modal
          show={showVideoModal}
          onHide={() => setShowVideoModal(false)}
          size="lg"
          centered
        >
          <Modal.Header closeButton style={{ borderBottom: 'none', paddingBottom: 0 }}>
            <Modal.Title style={{ color: '#1a365d', fontWeight: 'bold' }}>
              Why You NEED to Codify Your IP
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-0">
            <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden' }}>
              <iframe
                src="https://player.vimeo.com/video/1159682892?autoplay=1&title=0&byline=0&portrait=0"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: 'none'
                }}
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                title="Why You Need to Codify Your IP"
              />
            </div>
          </Modal.Body>
        </Modal>
      </Container>
    );
  }

  // ==========================================
  // RENDER: KNOWLEDGE GRAPH CHECK OFFER
  // ==========================================
  if (phase === 'kg_check_offer') {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <Card className="shadow">
              <Card.Body className="p-5">
                <div className="text-center mb-4">
                  <span style={{ fontSize: '4rem' }}>🔍</span>
                </div>
                <h2 className="text-center mb-3" style={{ color: '#1a365d' }}>
                  Check Your AI Visibility
                </h2>
                <p className="text-center text-muted mb-4">
                  Would you like us to check your status in the AI knowledge graph?
                </p>

                <div className="p-4 mb-4" style={{ backgroundColor: '#f0fff4', borderRadius: '8px', borderLeft: '4px solid #2f855a' }}>
                  <h5 className="mb-3" style={{ color: '#276749' }}>Why This Matters</h5>
                  <p className="mb-2" style={{ color: '#4a5568' }}>
                    Being cited by AI platforms like ChatGPT, Claude, and Perplexity doesn't just help your reputation —
                    it can drive high-quality leads directly to your business.
                  </p>
                  <ul className="mb-0" style={{ color: '#4a5568' }}>
                    <li>See what AI currently knows about you</li>
                    <li>Discover if you're listed among industry experts</li>
                    <li>Understand the gap between where you are and where you could be</li>
                  </ul>
                </div>

                <Row className="g-3 mb-4">
                  <Col md={6}>
                    <Button
                      size="lg"
                      className="w-100"
                      onClick={() => handleKgCheckChoice(true)}
                      disabled={loading}
                      style={successButtonStyle}
                      onMouseEnter={(e) => Object.assign(e.target.style, successButtonHoverStyle)}
                      onMouseLeave={(e) => Object.assign(e.target.style, successButtonStyle)}
                    >
                      {loading ? <><Spinner size="sm" className="me-2" />Checking...</> : 'Yes, Check My Status'}
                    </Button>
                  </Col>
                  <Col md={6}>
                    <Button
                      size="lg"
                      variant="outline-secondary"
                      className="w-100"
                      onClick={() => handleKgCheckChoice(false)}
                      disabled={loading}
                    >
                      Skip for Now
                    </Button>
                  </Col>
                </Row>

                <p className="text-center text-muted small">
                  The check takes about 30 seconds. You can skip this and proceed directly to IP extraction.
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // ==========================================
  // RENDER: KNOWLEDGE GRAPH CHECK RESULTS
  // ==========================================
  if (phase === 'kg_check_results') {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <Card className="shadow">
              <Card.Body className="p-5">
                <h2 className="text-center mb-4" style={{ color: '#1a365d' }}>Your AI Visibility Check</h2>

                {searchLoading ? (
                  <div className="text-center py-4">
                    <Spinner animation="border" variant="primary" className="mb-3" />
                    <p className="text-muted">
                      Searching AI platforms to see what they know about you...
                    </p>
                    <p className="text-muted small">This takes about 30 seconds</p>
                  </div>
                ) : searchResults && !searchResults.error ? (
                  <>
                    <p className="text-center text-muted mb-4">
                      Here's what AI platforms currently know about you:
                    </p>

                    <Card className="mb-3" style={{ backgroundColor: '#f8fafc' }}>
                      <Card.Body>
                        <h6 className="text-muted mb-2">Search: "Who is {formData.name}?"</h6>
                        <p className="mb-0">{searchResults.name_search}</p>
                      </Card.Body>
                    </Card>

                    {searchResults.company_search && searchResults.company_search !== "No company provided" && (
                      <Card className="mb-3" style={{ backgroundColor: '#f8fafc' }}>
                        <Card.Body>
                          <h6 className="text-muted mb-2">Search: "What does {formData.company} do?"</h6>
                          <p className="mb-0">{searchResults.company_search}</p>
                        </Card.Body>
                      </Card>
                    )}

                    {searchResults.experts_search && searchResults.experts_search !== "No industry provided" && (
                      <Card className="mb-3" style={{ backgroundColor: '#f8fafc' }}>
                        <Card.Body>
                          <h6 className="text-muted mb-2">Search: "Who are the leading experts in {formData.industry}?"</h6>
                          <p className="mb-0">{searchResults.experts_search}</p>
                        </Card.Body>
                      </Card>
                    )}

                    <div className="mt-4 p-3" style={{ backgroundColor: '#fff5f5', borderRadius: '8px' }}>
                      <h5 className="mb-3">Analysis:</h5>
                      <p className="mb-2">
                        {searchResults.analysis?.ai_found_you
                          ? '✓ AI found information about you'
                          : '✗ AI has never heard of you'}
                      </p>
                      <p className="mb-2">
                        {searchResults.analysis?.listed_among_experts
                          ? '✓ You\'re listed among experts'
                          : '✗ You\'re not listed among experts in your field'}
                      </p>
                    </div>

                    <div className="mt-4 p-4 text-center" style={{ backgroundColor: '#f0fff4', borderRadius: '8px' }}>
                      <p className="mb-3">
                        <strong>This is what AI knows about you RIGHT NOW.</strong>
                      </p>
                      <p className="mb-4">
                        By documenting your intellectual property, you can change this.
                        Let's extract what makes you unique.
                      </p>
                      <Button
                        size="lg"
                        onClick={handleKgCheckContinue}
                        style={successButtonStyle}
                        onMouseEnter={(e) => Object.assign(e.target.style, successButtonHoverStyle)}
                        onMouseLeave={(e) => Object.assign(e.target.style, successButtonStyle)}
                      >
                        Continue to IP Extraction
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-muted mb-4">
                      We couldn't complete the AI search, but don't worry — you can still extract your IP.
                    </p>
                    <Button
                      size="lg"
                      onClick={handleKgCheckContinue}
                      style={successButtonStyle}
                      onMouseEnter={(e) => Object.assign(e.target.style, successButtonHoverStyle)}
                      onMouseLeave={(e) => Object.assign(e.target.style, successButtonStyle)}
                    >
                      Continue to IP Extraction
                    </Button>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // ==========================================
  // RENDER: DECISION GATE
  // ==========================================
  if (phase === 'decision_gate') {
    return <DecisionGate sessionId={sessionId} userName={formData.name} onSelectPath={handleSelectPath} loading={loading} error={error} />;
  }

  // ==========================================
  // RENDER: V1 WAKEUP PHASE
  // ==========================================
  if (phase === 'have_ip_wakeup') {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <Card className="shadow">
              <Card.Body className="p-5">
                <h2 className="text-center mb-4" style={{ color: '#1a365d' }}>The Wake-Up Call</h2>

                <div className="mb-4 p-4" style={{ backgroundColor: '#f0fff4', borderRadius: '8px', borderLeft: '4px solid #2f855a' }}>
                  <h5 className="mb-3" style={{ color: '#276749' }}>Why Documented IP Changes Everything</h5>
                  <ul className="mb-0" style={{ color: '#4a5568' }}>
                    <li><strong>AI platforms start citing you</strong></li>
                    <li><strong>Trust compounds</strong> — prospects see your methodology first</li>
                    <li><strong>Premium positioning</strong> — stop competing on price</li>
                    <li><strong>Business valuation increases</strong></li>
                  </ul>
                </div>

                {searchLoading ? (
                  <div className="text-center py-4">
                    <Spinner animation="border" variant="primary" className="mb-3" />
                    <p className="text-muted">Searching AI platforms to see what they know about you...</p>
                  </div>
                ) : searchResults && !searchResults.error ? (
                  <>
                    <Card className="mb-3" style={{ backgroundColor: '#f8fafc' }}>
                      <Card.Body>
                        <h6 className="text-muted mb-2">Search: "Who is {formData.name}?"</h6>
                        <p className="mb-0">{searchResults.name_search}</p>
                      </Card.Body>
                    </Card>

                    <div className="mt-4 p-4 text-center" style={{ backgroundColor: '#f0fff4', borderRadius: '8px' }}>
                      <p className="mb-4"><strong>This is what AI knows about you RIGHT NOW.</strong></p>
                      <Button size="lg" onClick={() => setPhase('have_ip_overview')} style={successButtonStyle}
                        onMouseEnter={(e) => Object.assign(e.target.style, successButtonHoverStyle)}
                        onMouseLeave={(e) => Object.assign(e.target.style, successButtonStyle)}>
                        Let's Change That
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-4">
                    <Button size="lg" onClick={() => setPhase('have_ip_overview')} style={successButtonStyle}
                      onMouseEnter={(e) => Object.assign(e.target.style, successButtonHoverStyle)}
                      onMouseLeave={(e) => Object.assign(e.target.style, successButtonStyle)}>
                      Continue to IP Extraction
                    </Button>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // ==========================================
  // RENDER: V1 OVERVIEW PHASE
  // ==========================================
  if (phase === 'have_ip_overview') {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <Card className="shadow">
              <Card.Body className="p-5">
                <h2 className="text-center mb-4" style={{ color: '#1a365d' }}>How This Works</h2>
                <p className="text-center text-muted mb-4">We'll extract your IP in 5 layers:</p>

                <div className="mb-4">
                  {['IDENTITY', 'PAIN PATTERNS', 'DECISION LOGIC', 'BOUNDARIES', 'LANGUAGE'].map((layer, idx) => (
                    <div key={idx} className="d-flex align-items-start mb-3">
                      <div className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-3" style={{ width: '32px', height: '32px', minWidth: '32px' }}>{idx + 1}</div>
                      <div><strong>{layer}</strong></div>
                    </div>
                  ))}
                </div>

                <Button size="lg" className="w-100" onClick={startV1Extraction} disabled={loading} style={brandButtonStyle}
                  onMouseEnter={(e) => Object.assign(e.target.style, brandButtonHoverStyle)}
                  onMouseLeave={(e) => Object.assign(e.target.style, brandButtonStyle)}>
                  {loading ? <><Spinner size="sm" className="me-2" />Starting...</> : 'Begin Extraction'}
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // ==========================================
  // RENDER: V1 EXTRACTION / CODIFY EXTRACTION
  // ==========================================
  if (phase === 'have_ip_extraction' || phase === 'codify_extraction') {
    return (
      <Container className="py-4">
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <Card className="shadow">
              <Card.Body className="p-4">
                {/* Progress */}
                <div className="mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <Badge style={{ backgroundColor: colors.redDark }}>Section {currentSection} of 5</Badge>
                    <span className="text-muted small">{progress}% complete</span>
                  </div>
                  <ProgressBar now={progress} style={{ height: '8px', backgroundColor: colors.greyLight }} />
                  <p className="mt-2 mb-0" style={{ color: colors.goldDark, fontWeight: '500' }}>{sectionTitle}</p>
                </div>

                {transitionedFromV2 && selectedIpAsset && (
                  <Alert variant="info" className="mb-3">
                    <small>Codifying: <strong>{selectedIpAsset.name}</strong></small>
                  </Alert>
                )}

                {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

                {/* Conversation */}
                <div className="mb-4 p-3" style={{ maxHeight: '400px', overflowY: 'auto', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  {conversationHistory.map((msg, idx) => (
                    <div key={idx} className={`mb-3 p-3 rounded ${msg.role === 'assistant' ? 'bg-white border' : 'bg-primary text-white ms-4'}`}
                      style={{ maxWidth: msg.role === 'user' ? '85%' : '100%', marginLeft: msg.role === 'user' ? 'auto' : '0' }}>
                      {msg.role === 'assistant' && <small className="text-muted d-block mb-1">AI Coach</small>}
                      <div style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</div>
                    </div>
                  ))}
                  <div ref={conversationEndRef} />
                </div>

                {coachingResponse && <Alert variant="warning" className="mb-3"><strong>Let's dig deeper:</strong> {attemptsRemaining} attempts remaining</Alert>}

                {samples.length > 0 && !coachingResponse && (
                  <div className="mb-3 p-3" style={{ backgroundColor: '#f8fafc', borderRadius: '8px' }}>
                    <p className="mb-2 text-muted small"><strong>Example answers:</strong></p>
                    {samples.map((sample, idx) => <p key={idx} className="mb-1 small text-muted" style={{ fontStyle: 'italic' }}>"{sample}"</p>)}
                  </div>
                )}

                {questionKey === 's5_naming' ? (
                  <div className="mb-3">
                    <div className="p-3 mb-2 rounded" style={{ border: namingChoice === 'have_name' ? '2px solid #0d6efd' : '2px solid #dee2e6', backgroundColor: namingChoice === 'have_name' ? '#e7f1ff' : '#fff', cursor: 'pointer' }}
                      onClick={() => { setNamingChoice('have_name'); setUserAnswer(''); }}>
                      <Form.Check type="radio" id="naming-have" name="namingChoice" label="Yes, I have a name for my methodology" checked={namingChoice === 'have_name'} onChange={() => { setNamingChoice('have_name'); setUserAnswer(''); }} />
                    </div>
                    {namingChoice === 'have_name' && <Form.Control type="text" placeholder="Enter your framework/methodology name..." value={proposedName} onChange={(e) => setProposedName(e.target.value)} className="ms-4 mb-3" style={{ maxWidth: '400px' }} autoFocus />}
                    <div className="p-3 rounded" style={{ border: namingChoice === 'need_help' ? '2px solid #0d6efd' : '2px solid #dee2e6', backgroundColor: namingChoice === 'need_help' ? '#e7f1ff' : '#fff', cursor: 'pointer' }}
                      onClick={() => { setNamingChoice('need_help'); setProposedName(''); }}>
                      <Form.Check type="radio" id="naming-help" name="namingChoice" label="I need help coming up with a name" checked={namingChoice === 'need_help'} onChange={() => { setNamingChoice('need_help'); setProposedName(''); }} />
                    </div>
                  </div>
                ) : (
                  <Form.Group className="mb-3">
                    <div className="position-relative">
                      <Form.Control as="textarea" rows={4} placeholder="Type your answer here..." value={userAnswer} onChange={(e) => setUserAnswer(e.target.value)} ref={answerInputRef} disabled={loading} style={{ paddingRight: '50px' }} />
                      <SpeechInput onStart={() => { answerBeforeSpeechRef.current = userAnswer; }}
                        onTranscript={(text) => { const prefix = answerBeforeSpeechRef.current ? answerBeforeSpeechRef.current + ' ' : ''; setUserAnswer(prefix + text); }}
                        onError={(msg) => setError(msg)} disabled={loading} className="position-absolute" style={{ right: '8px', bottom: '8px' }} size="sm" />
                    </div>
                  </Form.Group>
                )}

                <Button size="lg" className="w-100"
                  onClick={() => {
                    if (questionKey === 's5_naming') {
                      if (namingChoice === 'have_name' && proposedName.trim()) handleSubmitV1Answer(`NAME: ${proposedName.trim()}`);
                      else if (namingChoice === 'need_help') handleSubmitV1Answer('NEED_HELP');
                    } else {
                      handleSubmitV1Answer();
                    }
                  }}
                  disabled={loading || (questionKey === 's5_naming' ? (!namingChoice || (namingChoice === 'have_name' && !proposedName.trim())) : !userAnswer.trim())}
                  style={brandButtonStyle}
                  onMouseEnter={(e) => Object.assign(e.target.style, brandButtonHoverStyle)}
                  onMouseLeave={(e) => Object.assign(e.target.style, brandButtonStyle)}>
                  {loading ? <><Spinner size="sm" className="me-2" />Processing...</> : 'Continue'}
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // ==========================================
  // RENDER: V2 CATEGORY SELECTION
  // ==========================================
  if (phase === 'explore_category') {
    return (
      <Container className="py-4">
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <Card className="shadow">
              <Card.Body className="p-4">
                <h2 className="text-center mb-3" style={{ color: '#1a365d' }}>What Type of IP Might You Have?</h2>
                <p className="text-center text-muted mb-4">Select a category or let us help you discover.</p>

                {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

                <Row>
                  {IP_CATEGORIES.map((cat) => (
                    <Col md={6} key={cat.id} className="mb-3">
                      <Card className={`h-100 ${selectedCategory === cat.id ? 'border-primary' : ''}`}
                        style={{ cursor: 'pointer', borderWidth: selectedCategory === cat.id ? '2px' : '1px', backgroundColor: selectedCategory === cat.id ? '#f0f7ff' : '#fff' }}
                        onClick={() => handleCategorySelect(cat.id)}>
                        <Card.Body className="p-3">
                          <div className="d-flex align-items-start">
                            <span className="me-2" style={{ fontSize: '1.5rem' }}>{cat.icon}</span>
                            <div>
                              <h6 className="mb-1" style={{ color: selectedCategory === cat.id ? '#0d6efd' : '#1a365d' }}>{cat.shortName}</h6>
                              <p className="mb-0 small text-muted">{cat.description}</p>
                            </div>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>

                <Card className={`mt-3 ${discoveryMode ? 'border-warning' : ''}`}
                  style={{ cursor: 'pointer', borderWidth: discoveryMode ? '2px' : '1px', backgroundColor: discoveryMode ? '#fffbeb' : '#fff' }}
                  onClick={handleDiscoveryMode}>
                  <Card.Body className="p-3 text-center">
                    <h6 className="mb-1" style={{ color: discoveryMode ? '#d97706' : '#1a365d' }}>I'm Not Sure What I Have</h6>
                    <p className="mb-0 small text-muted">We'll help you discover your hidden IP.</p>
                  </Card.Body>
                </Card>

                <Button size="lg" className="w-100 mt-4" onClick={startV2Interview} disabled={loading || (!selectedCategory && !discoveryMode)} style={successButtonStyle}
                  onMouseEnter={(e) => Object.assign(e.target.style, successButtonHoverStyle)}
                  onMouseLeave={(e) => Object.assign(e.target.style, successButtonStyle)}>
                  {loading ? <><Spinner size="sm" className="me-2" />Starting...</> : 'Continue'}
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // ==========================================
  // RENDER: V2 INTERVIEW
  // ==========================================
  if (phase === 'explore_interview') {
    const currentQ = v2Questions[currentQuestionIndex];
    return (
      <Container className="py-4">
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <Card className="shadow">
              <Card.Body className="p-4">
                <div className="mb-4">
                  <Badge style={{ backgroundColor: colors.redDark }}>{currentQ?.type === 'warmup' ? 'Warm-up' : 'Discovery'}</Badge>
                  <span className="text-muted small ms-2">Question {currentQuestionIndex + 1} of {v2Questions.length}</span>
                </div>

                {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

                <div className="mb-4 p-3" style={{ maxHeight: '350px', overflowY: 'auto', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  {conversationHistory.map((msg, idx) => (
                    <div key={idx} className={`mb-3 p-3 rounded ${msg.role === 'assistant' ? 'bg-white border' : 'bg-primary text-white ms-4'}`}
                      style={{ maxWidth: msg.role === 'user' ? '85%' : '100%', marginLeft: msg.role === 'user' ? 'auto' : '0' }}>
                      {msg.role === 'assistant' && <small className="text-muted d-block mb-1">AI Coach</small>}
                      <div style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</div>
                    </div>
                  ))}
                  <div ref={conversationEndRef} />
                </div>

                <Form.Group className="mb-3">
                  <Form.Control as="textarea" rows={4} placeholder="Type your answer here..." value={currentV2Answer} onChange={(e) => setCurrentV2Answer(e.target.value)} disabled={loading} />
                </Form.Group>

                <Button size="lg" className="w-100" onClick={handleSubmitV2Answer} disabled={loading || !currentV2Answer.trim()} style={brandButtonStyle}
                  onMouseEnter={(e) => Object.assign(e.target.style, brandButtonHoverStyle)}
                  onMouseLeave={(e) => Object.assign(e.target.style, brandButtonStyle)}>
                  {loading ? <><Spinner size="sm" className="me-2" />Processing...</> : 'Continue'}
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // ==========================================
  // RENDER: V2 OUTPUT (WITH CODIFY BUTTONS)
  // ==========================================
  if (phase === 'explore_output') {
    return (
      <Container className="py-4">
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <div className="text-center mb-4 p-4" style={{ backgroundColor: '#f0fff4', borderRadius: '8px' }}>
              <h2 style={{ color: '#1a365d' }}>Your IP Discovery Complete!</h2>
              <p className="mb-0">Here's the intellectual property we discovered.</p>
            </div>

            {v2Output?.ip_assets?.length > 0 && (
              <Card className="shadow mb-4">
                <Card.Header className="bg-primary text-white py-3">
                  <h4 className="mb-0">Discovered IP Assets</h4>
                </Card.Header>
                <Card.Body className="p-4">
                  {v2Output.ip_assets.map((asset, idx) => (
                    <div key={idx} className={idx > 0 ? 'mt-4 pt-4 border-top' : ''}>
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                          <Badge className="me-2" style={{ backgroundColor: asset.value === 'high' ? '#2f855a' : '#d69e2e' }}>
                            {asset.value === 'high' ? 'High Value' : 'Medium Value'}
                          </Badge>
                          <Badge style={{ backgroundColor: colors.greyMid }}>
                            {IP_CATEGORIES.find(c => c.id === asset.category)?.shortName || asset.category}
                          </Badge>
                        </div>
                        <Button size="sm" variant="outline-success" onClick={() => handleSelectIpToCodify(asset)}>
                          Codify This IP
                        </Button>
                      </div>
                      <h5 className="mb-2">{asset.name}</h5>
                      <p className="text-muted mb-2">{asset.description}</p>
                      {asset.next_steps && <div className="small" style={{ color: '#2f855a' }}><strong>Next Steps:</strong> {asset.next_steps}</div>}
                    </div>
                  ))}
                </Card.Body>
              </Card>
            )}

            <Card className="shadow">
              <Card.Body className="p-4 text-center">
                <h5 className="mb-3">Ready to turn one of these into a complete framework?</h5>
                <p className="text-muted mb-3">Click "Codify This IP" on any asset above to start the deep extraction process.</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // ==========================================
  // RENDER: TRANSITION SCREEN
  // ==========================================
  if (phase === 'transition') {
    return <TransitionScreen selectedIp={selectedIpAsset} userName={formData.name} onContinue={handleStartCodification} loading={loading} />;
  }

  // ==========================================
  // RENDER: NAMING PHASE
  // ==========================================
  if (phase === 'naming') {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <Card className="shadow">
              <Card.Body className="p-5">
                <ProgressBar now={100} style={{ height: '8px', backgroundColor: colors.greyLight }} className="mb-3" />
                <h3 className="mb-4">Let's Name Your Methodology</h3>

                {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

                {synopsis && (
                  <Card className="mb-4" style={{ backgroundColor: '#f0fff4', border: '1px solid #9ae6b4' }}>
                    <Card.Body>
                      <h5 className="mb-3" style={{ color: '#276749' }}>Here's What We Extracted:</h5>
                      {synopsis.expertise && <div className="mb-2"><strong>Your Expertise:</strong> <p className="mb-0 mt-1">{synopsis.expertise}</p></div>}
                      {synopsis.insight && <div className="mb-2"><strong>Pattern You See:</strong> <p className="mb-0 mt-1">{synopsis.insight}</p></div>}
                    </Card.Body>
                  </Card>
                )}

                {namingOptions ? (
                  <>
                    <h6 className="text-muted mb-2">Descriptive Names</h6>
                    <div className="mb-3">
                      {namingOptions.descriptive?.map((name, idx) => (
                        <Form.Check key={`desc-${idx}`} type="radio" id={`name-desc-${idx}`} label={name} name="frameworkName" checked={selectedName === name} onChange={() => setSelectedName(name)} className="mb-2" />
                      ))}
                    </div>

                    <h6 className="text-muted mb-2">Action-Oriented Names</h6>
                    <div className="mb-3">
                      {namingOptions.action_oriented?.map((name, idx) => (
                        <Form.Check key={`action-${idx}`} type="radio" id={`name-action-${idx}`} label={name} name="frameworkName" checked={selectedName === name} onChange={() => setSelectedName(name)} className="mb-2" />
                      ))}
                    </div>

                    <h6 className="text-muted mb-2">Metaphor-Based Names</h6>
                    <div className="mb-3">
                      {namingOptions.metaphor_based?.map((name, idx) => (
                        <Form.Check key={`meta-${idx}`} type="radio" id={`name-meta-${idx}`} label={name} name="frameworkName" checked={selectedName === name} onChange={() => setSelectedName(name)} className="mb-2" />
                      ))}
                    </div>

                    <hr />
                    <Form.Check type="radio" id="name-custom" label="I have my own name in mind" name="frameworkName" checked={selectedName === 'custom'} onChange={() => setSelectedName('custom')} className="mb-2" />
                    {selectedName === 'custom' && <Form.Control type="text" placeholder="Enter your framework name..." value={customName} onChange={(e) => setCustomName(e.target.value)} className="mt-2 mb-3" />}
                  </>
                ) : (
                  <div className="text-center py-4"><Spinner animation="border" /><p className="mt-2 text-muted">Generating naming options...</p></div>
                )}

                <Button size="lg" className="w-100 mt-3" onClick={handleSetName} disabled={loading || (!selectedName || (selectedName === 'custom' && !customName.trim()))} style={successButtonStyle}
                  onMouseEnter={(e) => Object.assign(e.target.style, successButtonHoverStyle)}
                  onMouseLeave={(e) => Object.assign(e.target.style, successButtonStyle)}>
                  {loading ? <><Spinner size="sm" className="me-2" />Finalizing...</> : 'Finalize My Framework'}
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // ==========================================
  // RENDER: OUTPUT PHASE
  // ==========================================
  if (phase === 'output') {
    if (loading || !output) {
      return (
        <Container className="py-5">
          <Row className="justify-content-center">
            <Col md={8} className="text-center">
              <Spinner animation="border" variant="primary" className="mb-3" />
              <p>Generating your IP framework...</p>
            </Col>
          </Row>
        </Container>
      );
    }

    return (
      <Container className="py-4">
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <div className="text-center mb-4 p-4" style={{ backgroundColor: '#f0fff4', borderRadius: '8px' }}>
              <h2 style={{ color: '#1a365d' }}>Congratulations!</h2>
              <p className="mb-0">You've just codified your intellectual property.</p>
            </div>

            {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

            <Card className="shadow mb-4">
              <Card.Header className="bg-primary text-white text-center py-3">
                <h3 className="mb-0">{output.frameworkName}</h3>
              </Card.Header>
              <Card.Body className="p-4">
                <h5 className="text-muted mb-2">What It Is</h5>
                <p className="mb-4">{output.whatItIs}</p>

                <h5 className="text-muted mb-2">Your Core Insight</h5>
                <p className="mb-4">{output.coreInsight}</p>

                <h5 className="text-muted mb-2">Your Decision Framework</h5>
                <p>{output.decisionFramework}</p>
                <p className="mb-4"><strong>Required Sequence:</strong> {output.requiredSequence}</p>

                <h5 className="text-muted mb-2">Domains You Synthesize ({output.domainCount})</h5>
                <div className="mb-4">{output.domains && output.domains.split(', ').map((domain, idx) => <div key={idx}>• {domain}</div>)}</div>

                <h5 className="text-muted mb-2">Who It's For</h5>
                <p className="mb-4">{output.whoItsFor}</p>

                <h5 className="text-muted mb-2">Who It's NOT For</h5>
                <div className="mb-4" style={{ whiteSpace: 'pre-line' }}>{output.notFor}</div>
              </Card.Body>
            </Card>

            {/* PDF Section */}
            <Card className="mb-4" style={{ backgroundColor: '#f8fafc' }}>
              <Card.Body className="p-4 text-center">
                <h5 className="mb-3">Get Your Framework as a PDF</h5>

                {!pdfSent && !emailError && (
                  <Button size="lg" onClick={() => handleSendPdf(false)} disabled={pdfSending} style={brandButtonStyle}
                    onMouseEnter={(e) => Object.assign(e.target.style, brandButtonHoverStyle)}
                    onMouseLeave={(e) => Object.assign(e.target.style, brandButtonStyle)}>
                    {pdfSending ? <><Spinner size="sm" className="me-2" />Sending...</> : <>Email PDF to {formData.email}</>}
                  </Button>
                )}

                {emailError && !pdfSent && (
                  <div className="mt-3">
                    <Alert variant="warning">{emailError}</Alert>
                    <div className="d-flex gap-2 justify-content-center">
                      <Form.Control type="email" placeholder="your@email.com" value={updatedEmail} onChange={(e) => setUpdatedEmail(e.target.value)} style={{ maxWidth: '300px' }} />
                      <Button onClick={() => handleSendPdf(true)} disabled={pdfSending || !updatedEmail.trim()} style={brandButtonStyle}>Retry</Button>
                    </div>
                  </div>
                )}

                {pdfSent && <Alert variant="success">PDF sent to {formData.email}! Check your inbox.</Alert>}
              </Card.Body>
            </Card>

            {/* CTA */}
            <Card className="shadow">
              <Card.Body className="p-4">
                <h4 className="mb-3">You Have More IP Than You Realize</h4>
                <p className="text-muted mb-4">Most experts have 5-10 pieces of valuable IP. The Command Center helps you extract ALL of it, build products around each one, and create marketing systems that sell them on autopilot.</p>
                <Button size="lg" href="https://www.marketdomination.solutions/ai-profit-engineer/" target="_blank" className="w-100" style={successButtonStyle}
                  onMouseEnter={(e) => Object.assign(e.target.style, successButtonHoverStyle)}
                  onMouseLeave={(e) => Object.assign(e.target.style, successButtonStyle)}>
                  Learn More About The Command Center →
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  return null;
}

export default MindToMethodUnified;
